import { AppState } from '@client/model/store';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// Navigation type - exported here to avoid circular imports
export interface LtcFinancialPlanningNavigation {
  currentStep: LtcFinancialPlanningStep;
  currentSubStep: string | null;
  goToStep: (step: LtcFinancialPlanningStep, substep?: string) => void;
  goNext: () => void;
  goPrev: () => void;
  progressBarStep: number;
  canGoPrev: boolean;
  canGoNext: boolean;
}

export const LTC_FINANCIAL_PLANNING_STEPS: LtcFinancialPlanningStep[] = [
  'assets',
  'priorities',
  'account-details',
  'coverage-assessment',
  'preferences',
  'policy-types',
  'policies',
  'funding',
  'comparison',
  'complete',
];

export const STEP_TO_PROGRESS_BAR: Record<LtcFinancialPlanningStep, number> = {
  'assets': 1,
  'priorities': 1,
  'account-details': 1,
  'coverage-assessment': 1,
  'preferences': 2,
  'policy-types': 3,
  'policies': 3,
  'funding': 4,
  'comparison': 4,
  'complete': 4,
};

export function getStepIndex(step: LtcFinancialPlanningStep): number {
  return LTC_FINANCIAL_PLANNING_STEPS.indexOf(step);
}

export function getNextStep(step: LtcFinancialPlanningStep): LtcFinancialPlanningStep | null {
  const index = getStepIndex(step);
  return index < LTC_FINANCIAL_PLANNING_STEPS.length - 1
    ? LTC_FINANCIAL_PLANNING_STEPS[index + 1]
    : null;
}

export function getPrevStep(step: LtcFinancialPlanningStep): LtcFinancialPlanningStep | null {
  const index = getStepIndex(step);
  return index > 0 ? LTC_FINANCIAL_PLANNING_STEPS[index - 1] : null;
}

export function getProgressBarStep(step: LtcFinancialPlanningStep): number {
  return STEP_TO_PROGRESS_BAR[step] ?? 1;
}

export const PROGRESS_STEPS = [
  { id: 1, name: 'Your Assets' },
  { id: 2, name: 'Preferences' },
  { id: 3, name: 'Options' },
  { id: 4, name: 'Your Plan' },
];

// Validation messages for each step
export const STEP_VALIDATION: Record<
  LtcFinancialPlanningStep,
  { validate: (state: AppState['ltcFinancialPlanning']) => boolean; errorMessage: string }
> = {
  'assets': {
    validate: state => state.selectedAssets.length > 0,
    errorMessage: 'Please select at least one asset to continue',
  },
  'priorities': {
    validate: () => true, // Optional step - user may not want to protect specific assets
    errorMessage: '',
  },
  'account-details': {
    validate: state =>
      state.selectedAssets.every(asset => {
        const detail = state.accountDetails[asset];
        return detail && detail.isComplete === true;
      }),
    errorMessage: 'Please complete all account details to continue',
  },
  'coverage-assessment': {
    validate: () => true, // Informational step
    errorMessage: '',
  },
  'preferences': {
    validate: () => true, // Has its own navigation
    errorMessage: '',
  },
  'policy-types': {
    validate: state => state.selectedPolicyTypes.length > 0,
    errorMessage: 'Please select at least one policy type to continue',
  },
  'policies': {
    validate: state => state.chosenPolicy !== null,
    errorMessage: 'Please select a policy to continue',
  },
  'funding': {
    validate: () => true, // TODO: Add validation if needed
    errorMessage: '',
  },
  'comparison': {
    validate: () => true, // Informational step
    errorMessage: '',
  },
  'complete': {
    validate: () => true, // Final step
    errorMessage: '',
  },
};

export function getPhaseProgress(phaseId: number, currentStep: LtcFinancialPlanningStep): number {
  const currentPhase = STEP_TO_PROGRESS_BAR[currentStep];

  // Past this phase = 100%
  if (currentPhase > phaseId) return 100;

  // Haven't reached this phase = 0%
  if (currentPhase < phaseId) return 0;

  // Currently in this phase - calculate based on completed steps
  const stepsInPhase = LTC_FINANCIAL_PLANNING_STEPS.filter(
    s => STEP_TO_PROGRESS_BAR[s] === phaseId,
  );
  const currentStepIndex = getStepIndex(currentStep);
  const phaseStartIndex = getStepIndex(stepsInPhase[0]);

  // Steps completed = current position minus phase start (0 on first step)
  const completedInPhase = currentStepIndex - phaseStartIndex;

  return (completedInPhase / stepsInPhase.length) * 100;
}

export function useLtcFinancialPlanningNavigation() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentStep = (searchParams.get('step') as LtcFinancialPlanningStep) || 'assets';
  const currentSubStep = searchParams.get('substep'); // For pages with multiple questions

  const goToStep = useCallback(
    (step: LtcFinancialPlanningStep, substep?: string) => {
      const params = new URLSearchParams(searchParams);
      params.set('step', step);
      if (substep) {
        params.set('substep', substep);
      } else {
        params.delete('substep');
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const goNext = useCallback(() => {
    const nextStep = getNextStep(currentStep);
    if (nextStep) {
      goToStep(nextStep);
    }
  }, [currentStep, goToStep]);

  const goPrev = useCallback(() => {
    const prevStep = getPrevStep(currentStep);
    if (prevStep) {
      goToStep(prevStep);
    }
  }, [currentStep, goToStep]);

  return {
    currentStep,
    currentSubStep,
    goToStep,
    goNext,
    goPrev,
    progressBarStep: getProgressBarStep(currentStep),
    canGoPrev: getPrevStep(currentStep) !== null,
    canGoNext: getNextStep(currentStep) !== null,
  };
}
